import {RootState} from "../../../../reducers";
import {getSession} from "../../../services/Session/reducer";
import {getStoreConfig} from "../../../services/Store/reducer";
import {connect, DispatchProp} from "react-redux";
import {useTranslation, withTranslation} from "react-i18next";
import {Offcanvas, OffcanvasBody, OffcanvasHeader, OffcanvasTitle} from "react-bootstrap";
import React, {useState} from "react";
import ErrorFormAlertView from "../../Common/ErrorFormAlertView";
import PickUpDateSectionDrawer from "./PickUpDateSectionDrawer";
import {ReactComponent as CalendarIco} from "../../../../images/icons/calendar.svg";
import {ReactComponent as ClockIco} from "../../../../images/icons/clock.svg";
import DateService from "../../../services/Common/Date/DateService";
import {DateHelper} from "../../../services/Common/Date/DateHelper";
import AvailableHelper from "../../../services/Menu/services/Availability/AvailableHelper";
import IframeWidget from "../../../utils/IframeWidget";
import {getPickUpDateDrawerDateOptions} from "./utils/pickUpDateDrawerUtils";
import HoursHelper from "../../../services/Menu/services/Availability/HoursHelper";
import {ConfigHoursApi} from "../../../services/Api/types";

interface OwnProps {
    show: boolean;
    onHide: () => void;
    title?: string;
    dates: { date_from: Date, date_to: Date }[];
    weekDays?: string[];
    orderType?: string;
    onSave: (date: any) => Promise<void>;
    buttonSaveTitle?: string;
    value?: any;
    availabilities: any[];
    higherZIndex?: boolean;
    withoutAsSoonAsPossibleOption?: true;
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps & DispatchProp;

function PickUpDateDrawer({
                              show,
                              onHide,
                              title,
                              session,
                              dates,
                              weekDays,
                              availabilities,
                              orderType,
                              buttonSaveTitle,
                              onSave,
                              value,
                              higherZIndex,
                              withoutAsSoonAsPossibleOption,
                              config
                          }: Props) {
    const {t} = useTranslation();
    const dateOptions = getPickUpDateDrawerDateOptions(session, config, t, dates, weekDays);
    const [errors, setErrors] = useState<any[]>([]);
    const [date, setDate] = useState(dateOptions.length === 1 ? dateOptions[0].value : "");
    const [time, setTime] = useState('');
    const [loading, setLoading] = useState(false);
    const [showItemsWarning, setShowItemsWarning] = useState(false);

    const getParsedTime = (i: number) => {
        return (i >= 24) ? "00" : (i < 10) ? "0" + i : i;
    }

    const isMinuteAvailable = (i: number, minHours: number, maxHours: number, minMinutes: number, maxMinutes: number, time: number) => {
        if (i !== minHours && i !== maxHours) {
            return true;
        }
        if (i === minHours && minMinutes > time) {
            return false;
        }
        if (i === maxHours && maxMinutes < time) {
            return false;
        }

        return true;
    }

    const getTimeOptions = (dateValue?: string) => {
        const today = DateService.now();
        let minTodayHour = 0;
        let minTodayMinute = 0;
        const newDateValue = dateValue ?? date;

        if (newDateValue === "" || newDateValue === DateHelper.formatOnlyDate(today)) {
            const todayWithMinStoreMinutes = session ? AvailableHelper.getAddMinutesToPickDateFromNow(orderType, session.estimated_preparation_duration, session.estimated_delivery_duration) : today;
            minTodayHour = todayWithMinStoreMinutes.getHours();
            minTodayMinute = todayWithMinStoreMinutes.getMinutes();
            if (DateHelper.formatOnlyDate(todayWithMinStoreMinutes) != DateHelper.formatOnlyDate(today)) {
                minTodayHour = 24;
                minTodayMinute = 0;
            }
        }

        const options: { value: string, label: string, disabled: boolean }[] = [];
        const max = 24;
        const availableHours: ConfigHoursApi[] = AvailableHelper.getHoursAvailable(availabilities, DateHelper.fromString(newDateValue + " 00:00:00")) ?? [];
        const groupedHours = HoursHelper.getOpenHoursGroupedByWeekday(availableHours);
        const allGroupedHours = Object.entries(groupedHours).map(([_, hours]) => hours).flat();
        const hours = HoursHelper.getMergedHourRanges(allGroupedHours)

        if (!hours || hours.length <= 0) return options;

        hours.forEach((hour) => {
            const minTime = hour.hour_from;
            const maxTime = hour.hour_to;
            let minHours = minTime ? parseInt(minTime.split(":")[0]) : 0;
            let maxHours = maxTime ? parseInt(maxTime.split(":")[0]) : 23;
            let minMinutes = minTime ? parseInt(minTime.split(":")[1]) : 0;
            let maxMinutes = maxTime ? parseInt(maxTime.split(":")[1]) : 59;

            if (maxHours <= 0) {
                maxHours = 24;
            }

            if (maxMinutes === 0) {
                maxMinutes = 59;
                maxHours = maxHours - 1;
            }
            const minHourWithEstimate = AvailableHelper.getAddMinutesToPickDate(orderType, session.estimated_preparation_duration, session.estimated_delivery_duration);

            if (minHourWithEstimate > 0) {
                minMinutes = minMinutes + minHourWithEstimate;
                if (minMinutes >= 60) {
                    const extraHours = Math.floor(minMinutes / 60);
                    minHours += extraHours;
                    minMinutes %= 60; // Keep the remaining minutes less than 60
                }
                const maxHourWithEstimate = minHourWithEstimate > 15 ? minHourWithEstimate - 15 : 0;
                maxMinutes = maxMinutes - maxHourWithEstimate;
                if (maxMinutes <= 0) {
                    const extraHours = Math.floor(maxMinutes / 60);
                    maxHours -= extraHours;
                    maxMinutes %= 60; // Keep the remaining minutes less than 60
                }
            }

            if (minHours < minTodayHour || (minHours === minTodayHour && minMinutes < minTodayMinute)) {
                minHours = minTodayHour;
                minMinutes = minTodayMinute;
            }

            maxHours = maxHours <= max ? maxHours : max;
            for (let i = minHours; i <= maxHours; i++) {
                const seconds = getParsedTime(i);
                const firstOptionAvailable = isMinuteAvailable(i, minHours, maxHours, minMinutes, maxMinutes, 0);
                const secondOptionAvailable = isMinuteAvailable(i, minHours, maxHours, minMinutes, maxMinutes, 15);
                const thirdOptionAvailable = isMinuteAvailable(i, minHours, maxHours, minMinutes, maxMinutes, 30);
                const fourthOptionAvailable = isMinuteAvailable(i, minHours, maxHours, minMinutes, maxMinutes, 45);

                if (firstOptionAvailable || secondOptionAvailable || thirdOptionAvailable || fourthOptionAvailable) {
                    options.push({
                        value: `${seconds}:00`,
                        label: `${seconds}:00`,
                        disabled: !firstOptionAvailable
                    })

                    options.push({
                        value: `${seconds}:15`,
                        label: `${seconds}:15`,
                        disabled: !secondOptionAvailable
                    })

                    options.push({
                        value: `${seconds}:30`,
                        label: `${seconds}:30`,
                        disabled: !thirdOptionAvailable
                    })

                    options.push({
                        value: `${seconds}:45`,
                        label: `${seconds}:45`,
                        disabled: !fourthOptionAvailable
                    })
                }
            }
        });

        return options;
    }

    const handleChangeDate = (value: string) => {
        setTime("");
        setShowItemsWarning(false);
        setDate(value);
    }

    const handleChangeTime = (value: string) => {
        setTime(value);
    }

    const handleClearDate = async () => {
        setLoading(true);
        setErrors([]);
        setTime("");
        setDate("");
        await save(undefined);
    }

    const handleSave = async () => {
        const errors = [];
        const today = DateService.now();
        let minTodayHour = 0;
        let minTodayMinute = 0;

        if (date === "" || date === DateHelper.formatOnlyDate(today)) {
            const todayWithMinStoreMinutes = session ? AvailableHelper.getAddMinutesToPickDateFromNow(orderType, session.estimated_preparation_duration, session.estimated_delivery_duration) : today;
            minTodayHour = todayWithMinStoreMinutes.getHours();
            minTodayMinute = todayWithMinStoreMinutes.getMinutes();
            if (DateHelper.formatOnlyDate(todayWithMinStoreMinutes) != DateHelper.formatOnlyDate(today)) {
                minTodayHour = 24;
                minTodayMinute = 0;
            }
            const hourSplit = Number(time.split(":")[0]);
            const minuteSplit = Number(time.split(":")[1]);
            if (minTodayHour > hourSplit || (hourSplit == minTodayHour && minTodayMinute > minuteSplit)) {
                errors.push({
                    "message": t("constraints.internal.time_must_be_not_empty"),
                    "code": "time_must_be_not_empty"
                })
            }
        }

        if (errors.length > 0) {
            setErrors(errors);
            setLoading(false);
            return;
        }
        await save(DateHelper.fromString(date + " " + time + ":00"));
    }


    const save = async (date: any) => {
        setErrors([]);
        setLoading(true);
        try {
            await onSave(date);
            setLoading(false);
            IframeWidget.onHide(onHide)
        } catch (error: any) {
            setErrors(error.errors);
            setLoading(false);
        }
    }

    const getDefaultTime = (today: any) => {
        let hours = today.getHours();
        let minutes = today.getMinutes();

        if (minutes > 0) {
            if (minutes <= 15) {
                minutes = 15;
            } else if (minutes <= 30) {
                minutes = 30;
            } else if (minutes <= 45) {
                minutes = 45;
            } else if (minutes <= 60) {
                minutes = 15;
                hours = hours + 1;
            }
        } else {
            minutes = "00";
        }
        return getParsedTime(hours) + ":" + minutes;
    }

    const onShow = () => {
        let states = {} as any;
        if (value) {
            let date = value;
            if (!(date instanceof Date)) {
                date = DateHelper.fromString(value);
            }

            if (availabilities === undefined || AvailableHelper.isAvailabile(availabilities, date)) {
                states.time = getDefaultTime(date);
                states.date = DateHelper.formatOnlyDate(date);
            }
        }

        const initialDate = states.date ? states.date : (dateOptions.length === 1 ? dateOptions[0].value : "");
        setDate(initialDate);
        setTime(states.time ? states.time : "");
    }

    const handleOnHide = () => {
        IframeWidget.onHide(onHide);
    }

    return (
        <Offcanvas placement="end" show={show} onHide={handleOnHide}
                   backdropClassName={higherZIndex ? "drawer-backdrop-with-higher-z-index" : ""}
                   className={`drawer pick-up-date-drawer ${higherZIndex ? "drawer-with-higher-z-index" : ""}`}
                   onShow={() => IframeWidget.onShow(onShow)}>
            <OffcanvasHeader closeButton aria-label={t("common.action.close")}>
                <OffcanvasTitle>
                    {title ? title : t("modules.date_modal.header.title")}
                </OffcanvasTitle>
            </OffcanvasHeader>
            <OffcanvasBody>
                <ErrorFormAlertView errors={errors}/>
                {showItemsWarning && <div className="alert alert-warning">
                    {t("constraints.internal.cart_items_availability")}
                </div>}
                <div className="d-flex flex-column gap-4">
                    <PickUpDateSectionDrawer currentValue={date} title={t("modules.date_modal.field.day.title")}
                                             icon={<CalendarIco/>}
                                             withoutAsSoonAsPossibleOption={withoutAsSoonAsPossibleOption}
                                             elements={dateOptions}
                                             handleClickElement={handleChangeDate}
                                             handleClearDate={handleClearDate}
                                             higherZIndex={higherZIndex}
                    />
                    {getTimeOptions().length > 0 && <PickUpDateSectionDrawer currentValue={time}
                                                                             title={t("modules.date_modal.field.hour.title")}
                                                                             times
                                                                             icon={<ClockIco/>}
                                                                             elements={getTimeOptions()}
                                                                             higherZIndex
                                                                             handleClickElement={handleChangeTime}
                    />}
                </div>
            </OffcanvasBody>
            <div className="offcanvas-footer">
                <div className="w-100 d-flex gap-3">
                    <button className="btn btn-default" onClick={handleOnHide}>{t("common.action.cancel")}</button>
                    <button className={"btn-spinner btn-primary btn btn-submit w-100" + (loading ? ' loading' : '')}
                            onClick={handleSave}
                            disabled={!date || !time}
                    >
                        <span className="spinner-border spinner-border-sm"/>
                        {buttonSaveTitle ?? t("common.action.save")}
                    </button>
                </div>
            </div>
        </Offcanvas>
    )
}

const mapStateToProps = (state: RootState) => ({
    config: getStoreConfig(state.config),
    session: getSession(state.session),
})

const connector = connect(mapStateToProps);
export default withTranslation()(connector(PickUpDateDrawer));